import { computed, signal } from "@preact/signals-core";
import { ApiRequestError } from "../lib/api";
import { bindErrorText, bindShow, bindValue, html, list } from "../lib/bind";
import { bindSelect, type SelectOptionGroup, setSelectOptions } from "../lib/formBind";
import { form } from "../lib/form";
import { parseMoneyInput } from "../lib/money";
import { currentScope } from "../lib/scope";
import { labelRow } from "../lib/table";
import { toast } from "../lib/toast";
import { type Client, listClients } from "../services/client";
import { type ProjectWithClient, listCompanyProjects } from "../services/project";
import {
  type TimesheetEntry,
  type TimesheetPeriodType,
  createTimesheetEntry,
  deleteTimesheetEntry,
  listTimesheetEntries,
  updateTimesheetEntry,
} from "../services/timesheet";

const PERIOD_SPAN: Record<Exclude<TimesheetPeriodType, "hours">, string> = {
  percent_day: "day",
  percent_week: "week",
  percent_month: "month",
};

/** Mirrors the server's display label (app/services/timesheet_pdf.py's _period_label) —
 * "as entered", not the converted figure, which has its own column. Exported for the
 * invoice editor's "Pull from timesheet" preview (04-frontend.md). */
export function periodLabel(entry: TimesheetEntry): string {
  if (entry.period_type === "hours") return `${entry.quantity} h`;
  return `${entry.quantity}% of ${PERIOD_SPAN[entry.period_type]}`;
}

function convertedLabel(entry: TimesheetEntry): string {
  if (entry.converted_quantity === null) return "—";
  return `${entry.converted_quantity} ${entry.rate_unit}`;
}

function statusBadge(entry: TimesheetEntry): { label: string; cls: string } {
  if (entry.locked) return { label: "Invoiced", cls: "badge-ok" };
  if (entry.invoice_id) return { label: "Attached to draft", cls: "badge-warn" };
  return { label: "Uninvoiced", cls: "badge-neutral" };
}

/** Groups a client-wide project list into `<optgroup>`-ready buckets, keyed by client —
 * projects already arrive ordered client name, project name (project_service.py). */
function groupProjectsByClient(projects: ProjectWithClient[]): SelectOptionGroup[] {
  const groups: SelectOptionGroup[] = [];
  const byClient = new Map<string, SelectOptionGroup>();
  for (const project of projects) {
    let group = byClient.get(project.client_id);
    if (!group) {
      group = { label: project.client_name, options: [] };
      byClient.set(project.client_id, group);
      groups.push(group);
    }
    group.options.push({
      value: project.id,
      label: project.code ? `${project.name} (${project.code})` : project.name,
    });
  }
  return groups;
}

function emptyEntryInput(projectId: string) {
  return {
    project_id: projectId,
    date: new Date().toISOString().slice(0, 10),
    description: "",
    period_type: "hours" as TimesheetPeriodType,
    quantity: "",
  };
}

const ENTRIES_PAGE_SIZE = 25;

export interface TimesheetPanelOptions {
  /** Pre-filters the list (and pre-selects the quick-add picker) to one project — used
   * by the project detail view's "same list/quick-add components as /timesheets,
   * pre-filtered" slice (04-frontend.md). The project filter/column stay hidden since
   * it's implied by the page. */
  fixedProjectId?: string;
  /** Full page vs an embedded panel (project detail view supplies its own heading). */
  showHeader?: boolean;
}

export function buildTimesheetPanel(opts: TimesheetPanelOptions = {}): Node {
  const showHeader = opts.showHeader ?? true;
  const fixedProjectId = opts.fixedProjectId ?? null;

  const entries = signal<TimesheetEntry[]>([]);
  const total = signal(0);
  const page = signal(1);
  const loadError = signal("");

  const projects = signal<ProjectWithClient[]>([]);
  const clients = signal<Client[]>([]);

  const dateFromFilter = signal("");
  const dateToFilter = signal("");
  const projectFilter = signal(fixedProjectId ?? "");
  const clientFilter = signal("");
  const uninvoicedOnly = signal(false);

  function reload(): void {
    listTimesheetEntries({
      date_from: dateFromFilter.value || undefined,
      date_to: dateToFilter.value || undefined,
      project_id: projectFilter.value || undefined,
      client_id: clientFilter.value || undefined,
      uninvoiced: uninvoicedOnly.value,
      page: page.value,
      page_size: ENTRIES_PAGE_SIZE,
    })
      .then((result) => {
        entries.value = result.items;
        total.value = result.total;
      })
      .catch((err: unknown) => {
        loadError.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  }
  function filtersChanged(): void {
    page.value = 1;
    reload();
  }
  reload();

  listCompanyProjects({ active: true, page_size: 200 })
    .then((result) => {
      projects.value = result.items;
    })
    .catch(() => {
      // Picker just stays empty — non-fatal.
    });
  listClients()
    .then((result) => {
      clients.value = result;
    })
    .catch(() => {
      // Filter dropdown just stays empty — non-fatal.
    });

  const quickAddForm = form(emptyEntryInput(fixedProjectId ?? ""));

  async function submitQuickAdd(event: Event): Promise<void> {
    event.preventDefault();
    if (!quickAddForm.fields.project_id.value) {
      toast.error("Pick a project.");
      return;
    }
    if (!quickAddForm.fields.quantity.value.trim()) {
      toast.error("Enter a quantity.");
      return;
    }
    try {
      await createTimesheetEntry(quickAddForm.fields.project_id.value, {
        date: quickAddForm.fields.date.value,
        description: quickAddForm.fields.description.value,
        period_type: quickAddForm.fields.period_type.value,
        quantity: quickAddForm.fields.quantity.value,
      });
      // Keep project + date (logging several entries for the same day is the common
      // case) — only description/quantity reset, ready for the next entry.
      quickAddForm.reset({
        ...quickAddForm.values(),
        description: "",
        quantity: "",
      });
      page.value = 1;
      reload();
      toast.success("Entry logged.");
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Failed to log entry");
    }
  }

  const fragment = html`<div class="${showHeader ? "view" : "timesheet-panel"}">
    <div class="card quick-add-card">
      <div class="card-label">Log time</div>
      <form class="form quick-add-form" onsubmit="${submitQuickAdd}">
        <div class="form-row">
          <div class="form-field quick-add-project-field">
            <label for="ts-project">Project</label>
            <select id="ts-project" required></select>
          </div>
          <div class="form-field">
            <label for="ts-date">Date</label>
            <input id="ts-date" type="date" required />
          </div>
          <div class="form-field">
            <label for="ts-period-type">Period</label>
            <select id="ts-period-type">
              <option value="hours">Hours</option>
              <option value="percent_day">% of day</option>
              <option value="percent_week">% of week</option>
              <option value="percent_month">% of month</option>
            </select>
          </div>
          <div class="form-field">
            <label for="ts-quantity">Quantity</label>
            <input id="ts-quantity" inputmode="decimal" required />
          </div>
        </div>
        <div class="form-field">
          <label for="ts-description">Task details</label>
          <input id="ts-description" required />
        </div>
        <div class="form-actions">
          <button class="btn btn-primary" type="submit">Add entry</button>
        </div>
      </form>
    </div>

    <p class="form-error"></p>

    <div class="card filters-card">
      <div class="form-row">
        <div class="form-field">
          <label for="ts-filter-from">From</label>
          <input id="ts-filter-from" type="date" />
        </div>
        <div class="form-field">
          <label for="ts-filter-to">To</label>
          <input id="ts-filter-to" type="date" />
        </div>
        <div class="form-field ts-filter-project-field">
          <label for="ts-filter-project">Project</label>
          <select id="ts-filter-project"><option value="">All</option></select>
        </div>
        <div class="form-field">
          <label for="ts-filter-client">Client</label>
          <select id="ts-filter-client"><option value="">All</option></select>
        </div>
        <div class="form-field checkbox-field">
          <label><input id="ts-filter-uninvoiced" type="checkbox" /> Uninvoiced only</label>
        </div>
      </div>
    </div>

    <div class="data-table timesheet-entries-table">
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Project</th>
            <th>Client</th>
            <th>Details</th>
            <th>Period</th>
            <th>Converted</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody class="entries-body"></tbody>
      </table>
    </div>

    <div class="pager">
      <button class="btn pager-prev" type="button">Previous</button>
      <span class="pager-label"></span>
      <button class="btn pager-next" type="button">Next</button>
    </div>
  </div>`;

  const projectSelect = fragment.querySelector<HTMLSelectElement>("#ts-project");
  const projectFieldEl = fragment.querySelector<HTMLElement>(".quick-add-project-field");
  const dateInput = fragment.querySelector<HTMLInputElement>("#ts-date");
  const periodTypeSelect = fragment.querySelector<HTMLSelectElement>("#ts-period-type");
  const quantityInput = fragment.querySelector<HTMLInputElement>("#ts-quantity");
  const descriptionInput = fragment.querySelector<HTMLInputElement>("#ts-description");
  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const filterFromInput = fragment.querySelector<HTMLInputElement>("#ts-filter-from");
  const filterToInput = fragment.querySelector<HTMLInputElement>("#ts-filter-to");
  const filterProjectSelect = fragment.querySelector<HTMLSelectElement>("#ts-filter-project");
  const filterProjectFieldEl = fragment.querySelector<HTMLElement>(".ts-filter-project-field");
  const filterClientSelect = fragment.querySelector<HTMLSelectElement>("#ts-filter-client");
  const filterUninvoicedInput = fragment.querySelector<HTMLInputElement>("#ts-filter-uninvoiced");
  const table = fragment.querySelector<HTMLTableElement>(".timesheet-entries-table table");
  const tbody = fragment.querySelector<HTMLTableSectionElement>(".entries-body");
  const pagerEl = fragment.querySelector<HTMLElement>(".pager");
  const pagerPrevBtn = fragment.querySelector<HTMLButtonElement>(".pager-prev");
  const pagerNextBtn = fragment.querySelector<HTMLButtonElement>(".pager-next");
  const pagerLabelEl = fragment.querySelector<HTMLElement>(".pager-label");

  if (
    !projectSelect ||
    !projectFieldEl ||
    !dateInput ||
    !periodTypeSelect ||
    !quantityInput ||
    !descriptionInput ||
    !errorEl ||
    !filterFromInput ||
    !filterToInput ||
    !filterProjectSelect ||
    !filterProjectFieldEl ||
    !filterClientSelect ||
    !filterUninvoicedInput ||
    !table ||
    !tbody ||
    !pagerEl ||
    !pagerPrevBtn ||
    !pagerNextBtn ||
    !pagerLabelEl
  ) {
    throw new Error("timesheets panel markup missing expected elements");
  }

  bindErrorText(errorEl, loadError);

  currentScope().effect(() => {
    setSelectOptions(projectSelect, groupProjectsByClient(projects.value), {
      placeholder: "Select a project…",
    });
    projectSelect.value = quickAddForm.fields.project_id.value;
  });
  projectSelect.addEventListener("change", () => {
    quickAddForm.fields.project_id.value = projectSelect.value;
  });
  bindValue(dateInput, quickAddForm.fields.date);
  bindSelect(periodTypeSelect, quickAddForm.fields.period_type);
  bindValue(quantityInput, quickAddForm.fields.quantity, {
    format: (v) => v,
    parse: (v) => parseMoneyInput(v) ?? v,
  });
  bindValue(descriptionInput, quickAddForm.fields.description);

  // A fixed project (project detail view) is implied by the page — the quick-add
  // picker locks to it rather than re-showing every company project.
  if (fixedProjectId) {
    projectFieldEl.style.display = "none";
    filterProjectFieldEl.style.display = "none";
  }

  currentScope().effect(() => {
    setSelectOptions(
      filterProjectSelect,
      projects.value.map((p) => ({ value: p.id, label: `${p.name} — ${p.client_name}` })),
      { placeholder: "All" },
    );
    filterProjectSelect.value = projectFilter.value;
  });
  currentScope().effect(() => {
    setSelectOptions(
      filterClientSelect,
      clients.value.map((c) => ({ value: c.id, label: c.name })),
      { placeholder: "All" },
    );
    filterClientSelect.value = clientFilter.value;
  });

  filterFromInput.addEventListener("change", () => {
    dateFromFilter.value = filterFromInput.value;
    filtersChanged();
  });
  filterToInput.addEventListener("change", () => {
    dateToFilter.value = filterToInput.value;
    filtersChanged();
  });
  filterProjectSelect.addEventListener("change", () => {
    projectFilter.value = filterProjectSelect.value;
    filtersChanged();
  });
  filterClientSelect.addEventListener("change", () => {
    clientFilter.value = filterClientSelect.value;
    filtersChanged();
  });
  filterUninvoicedInput.addEventListener("change", () => {
    uninvoicedOnly.value = filterUninvoicedInput.checked;
    filtersChanged();
  });

  const pageCount = computed(() => Math.max(1, Math.ceil(total.value / ENTRIES_PAGE_SIZE)));
  currentScope().effect(() => {
    pagerEl.style.display = pageCount.value > 1 ? "" : "none";
    pagerLabelEl.textContent = `Page ${String(page.value)} of ${String(pageCount.value)}`;
    pagerPrevBtn.disabled = page.value <= 1;
    pagerNextBtn.disabled = page.value >= pageCount.value;
  });
  pagerPrevBtn.addEventListener("click", () => {
    if (page.value > 1) {
      page.value -= 1;
      reload();
    }
  });
  pagerNextBtn.addEventListener("click", () => {
    if (page.value < pageCount.value) {
      page.value += 1;
      reload();
    }
  });

  function replaceEntry(fresh: TimesheetEntry): void {
    entries.value = entries.value.map((e) => (e.id === fresh.id ? fresh : e));
  }
  function removeEntry(id: string): void {
    entries.value = entries.value.filter((e) => e.id !== id);
    total.value = Math.max(0, total.value - 1);
  }

  list(
    tbody,
    entries,
    (e) => e.id,
    (e) => labelRow(buildEntryRow(e, replaceEntry, removeEntry), table),
  );

  return fragment;
}

export function timesheetsView(): Node {
  return buildTimesheetPanel({ showHeader: true });
}

function buildEntryRow(
  entry: TimesheetEntry,
  onChanged: (fresh: TimesheetEntry) => void,
  onDeleted: (id: string) => void,
): Element {
  const editing = signal(false);
  const notEditing = computed(() => !editing.value);
  const editable = computed(() => !entry.locked);

  const editForm = form({
    date: entry.date,
    description: entry.description,
    period_type: entry.period_type,
    quantity: entry.quantity,
  });

  function startEdit(): void {
    editForm.reset({
      date: entry.date,
      description: entry.description,
      period_type: entry.period_type,
      quantity: entry.quantity,
    });
    editing.value = true;
  }
  function cancelEdit(): void {
    editing.value = false;
  }

  async function saveEdit(): Promise<void> {
    try {
      const updated = await updateTimesheetEntry(entry.id, {
        date: editForm.fields.date.value,
        description: editForm.fields.description.value,
        period_type: editForm.fields.period_type.value,
        quantity: editForm.fields.quantity.value,
        updated_at: entry.updated_at,
      });
      onChanged(updated);
      toast.success("Entry updated.");
    } catch (err) {
      const message =
        err instanceof ApiRequestError && err.status === 412
          ? "This entry changed elsewhere — reload and retry."
          : err instanceof ApiRequestError
            ? err.body.message
            : "Update failed";
      toast.error(message);
    }
  }

  async function doDelete(): Promise<void> {
    if (!window.confirm("Delete this timesheet entry?")) return;
    try {
      await deleteTimesheetEntry(entry.id);
      onDeleted(entry.id);
      toast.success("Entry deleted.");
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Delete failed");
    }
  }

  const badge = statusBadge(entry);

  const row = html`<tr>
    <td>
      <span class="ts-date-display">${entry.date}</span>
      <input class="ts-edit-date ts-edit-field" type="date" />
    </td>
    <td>${entry.project_name}</td>
    <td>${entry.client_name}</td>
    <td>
      <span class="ts-description-display">${entry.description}</span>
      <input class="ts-edit-description ts-edit-field" />
    </td>
    <td>
      <span class="ts-period-display">${periodLabel(entry)}</span>
      <span class="ts-edit-field ts-edit-period-group">
        <select class="ts-edit-period-type">
          <option value="hours">Hours</option>
          <option value="percent_day">% of day</option>
          <option value="percent_week">% of week</option>
          <option value="percent_month">% of month</option>
        </select>
        <input class="ts-edit-quantity" inputmode="decimal" />
      </span>
    </td>
    <td>${convertedLabel(entry)}</td>
    <td><span class="badge ${badge.cls}">${badge.label}</span></td>
    <td class="ts-actions">
      <button class="btn ts-edit-btn" type="button">Edit</button>
      <button class="btn btn-primary ts-save-btn" type="button">Save</button>
      <button class="btn ts-cancel-btn" type="button">Cancel</button>
      <button class="btn btn-danger ts-delete-btn" type="button">Delete</button>
    </td>
  </tr>`.firstElementChild;
  if (!row) throw new Error("timesheet entry row template produced no element");

  const dateDisplay = row.querySelector<HTMLElement>(".ts-date-display");
  const descriptionDisplay = row.querySelector<HTMLElement>(".ts-description-display");
  const periodDisplay = row.querySelector<HTMLElement>(".ts-period-display");
  const editFieldEls = row.querySelectorAll<HTMLElement>(".ts-edit-field");
  const editDateInput = row.querySelector<HTMLInputElement>(".ts-edit-date");
  const editDescriptionInput = row.querySelector<HTMLInputElement>(".ts-edit-description");
  const editPeriodTypeSelect = row.querySelector<HTMLSelectElement>(".ts-edit-period-type");
  const editQuantityInput = row.querySelector<HTMLInputElement>(".ts-edit-quantity");
  const editBtn = row.querySelector<HTMLButtonElement>(".ts-edit-btn");
  const saveBtn = row.querySelector<HTMLButtonElement>(".ts-save-btn");
  const cancelBtn = row.querySelector<HTMLButtonElement>(".ts-cancel-btn");
  const deleteBtn = row.querySelector<HTMLButtonElement>(".ts-delete-btn");

  if (
    !dateDisplay ||
    !descriptionDisplay ||
    !periodDisplay ||
    !editDateInput ||
    !editDescriptionInput ||
    !editPeriodTypeSelect ||
    !editQuantityInput ||
    !editBtn ||
    !saveBtn ||
    !cancelBtn ||
    !deleteBtn
  ) {
    throw new Error("timesheet entry row markup missing expected elements");
  }

  bindValue(editDateInput, editForm.fields.date);
  bindValue(editDescriptionInput, editForm.fields.description);
  bindSelect(editPeriodTypeSelect, editForm.fields.period_type);
  bindValue(editQuantityInput, editForm.fields.quantity, {
    format: (v) => v,
    parse: (v) => parseMoneyInput(v) ?? v,
  });

  bindShow(dateDisplay, notEditing);
  bindShow(descriptionDisplay, notEditing);
  bindShow(periodDisplay, notEditing);
  for (const el of editFieldEls) bindShow(el, editing);
  bindShow(editBtn, computed(() => editable.value && notEditing.value));
  bindShow(saveBtn, editing);
  bindShow(cancelBtn, editing);
  bindShow(deleteBtn, computed(() => editable.value && notEditing.value));

  if (!editable.value) {
    deleteBtn.title = "Attached to an issued invoice — void it to re-bill";
  }

  editBtn.addEventListener("click", startEdit);
  cancelBtn.addEventListener("click", cancelEdit);
  saveBtn.addEventListener("click", () => {
    void saveEdit();
  });
  deleteBtn.addEventListener("click", () => {
    void doDelete();
  });

  return row;
}
